// src/utils/tenantPermissions.js
//
// Who, inside a tenant, is allowed to email paperwork and to change payment
// figures. A Super Admin sets this once in Management → Users, and it
// applies to everyone in the tenant.
//
// Why this file exists:
//
// These are tenant-wide settings, but they used to be stored inside a *company*
// document — and the company was chosen by whichever one Firestore happened to
// return first, which is document-ID order. Nobody picks those IDs; they are
// random. So the company holding a tenant's permissions was arbitrary, and worse,
// it changed whenever a company with a lower-sorting ID was added. A permission
// granted months ago would silently stop applying, with nothing to connect the
// two events.
//
// The tenant document is the correct home: there is exactly one per tenant, and
// its id is the tenantId, so nothing can drift. Reads fall back to the old
// company location so that no existing grant is lost — see getAllowedRoles.

import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Every tenant permission, and who may use it when a tenant has never set one.
 *
 * Super Admin appears in every default deliberately: an account must never be
 * able to lock itself out of its own paperwork.
 */
export const TENANT_PERMISSION_DEFAULTS = {
  canEmailInvoice: ['Super Admin'],
  canEditPaymentTerms: ['Super Admin'],
};

/** A user's roles, tolerating both the array and the older single-string shape. */
export function rolesOf(user) {
  if (!user) return [];
  if (Array.isArray(user.role)) return user.role.filter(Boolean);
  return user.role ? [user.role] : [];
}

const isNonEmptyList = (value) => Array.isArray(value) && value.length > 0;

/**
 * The roles allowed to use `key` in this tenant.
 *
 * Looked for in three places, in order:
 *   1. The tenant document — where new saves go, and the only place that cannot drift.
 *   2. Any company in the tenant still carrying the setting from before this moved.
 *      Note "any": the old code read whichever company sorted first, which is how
 *      settings became orphaned on a company nothing looked at. Searching for the
 *      company that actually holds the value recovers those.
 *   3. The default for that permission.
 *
 * A read failure falls through to the next source rather than throwing. Callers
 * decide what to do with the answer; they should not have to handle an exception
 * to render a button.
 */
export async function getAllowedRoles(tenantId, key) {
  const fallback = TENANT_PERMISSION_DEFAULTS[key] || ['Super Admin'];
  if (!tenantId) return fallback;

  try {
    const tenantSnap = await getDoc(doc(db, 'tenants', tenantId));
    const roles = tenantSnap.exists() ? tenantSnap.data()?.permissions?.[key] : null;
    if (isNonEmptyList(roles)) return roles;
  } catch (error) {
    console.warn(`Could not read tenant permission "${key}":`, error?.message);
  }

  try {
    const snapshot = await getDocs(
      query(collection(db, 'companies'), where('tenantId', '==', tenantId))
    );
    const holder = snapshot.docs.find(d => isNonEmptyList(d.data()?.permissions?.[key]));
    if (holder) return holder.data().permissions[key];
  } catch (error) {
    console.warn(`Could not read legacy company permission "${key}":`, error?.message);
  }

  return fallback;
}

/** Does this user hold `key` in their own tenant? */
export async function hasTenantPermission(user, key) {
  const allowed = await getAllowedRoles(user?.tenantId, key);
  return rolesOf(user).some(role => allowed.includes(role));
}
